// AudioCaptureSender.tsx
import React, { useState, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { SOCKET_CONFIG } from "../config/socket";
import { useGetSonometerQuery } from "sonometer/api/sonometer.api";
import { useParams } from "react-router-dom";

const AudioCaptureSender: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [currentDb, setCurrentDb] = useState(0);
  const [error, setError] = useState("");
  const { sonometerId } = useParams<{ sonometerId: string }>();

  const { data: sonometer } = useGetSonometerQuery(sonometerId!);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const lastSentRef = useRef(0);

  // EXACT SAME CONFIGURATION AS PYTHON
  const CALIBRATION_OFFSET = sonometer?.caliberationOffset || 138.0;
  const SAMPLERATE = 44100;
  const BLOCKSIZE = 1024;
  const INTERVAL = 200; // milliseconds (0.2 seconds)

  // Socket.IO connection
  useEffect(() => {
    socketRef.current = io(SOCKET_CONFIG.serverUrl, {
      path: SOCKET_CONFIG.path,
      transports: SOCKET_CONFIG.transports,
    });

    socketRef.current.on("connect", () => {
      console.log("✅ Connected to server");
      setIsConnected(true);
    });

    socketRef.current.on("disconnect", () => {
      console.log("❌ Disconnected from server");
      setIsConnected(false);
    });

    return () => {
      stopRecording();
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      setError("");

      // Stop existing recording if any
      stopRecording();

      // Request microphone access with exact same specs as Python
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: SAMPLERATE,
          channelCount: 1, // MONO like Python
        },
      });

      // Create audio context with exact same sample rate
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)({
        sampleRate: SAMPLERATE,
      });

      // Create analyser
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = BLOCKSIZE;
      analyserRef.current.smoothingTimeConstant = 0.0; // No smoothing like Python

      // Create microphone source
      microphoneRef.current =
        audioContextRef.current.createMediaStreamSource(stream);
      microphoneRef.current.connect(analyserRef.current);

      // Create script processor with EXACT same buffer size as Python
      scriptProcessorRef.current =
        audioContextRef.current.createScriptProcessor(
          BLOCKSIZE,
          1, // Input channels (MONO)
          1, // Output channels
        );

      analyserRef.current.connect(scriptProcessorRef.current);
      scriptProcessorRef.current.connect(audioContextRef.current.destination);

      // Audio processing callback - EXACT SAME ALGORITHM AS PYTHON
      scriptProcessorRef.current.onaudioprocess = (event) => {
        if (!socketRef.current || !socketRef.current.connected) return;

        const inputBuffer = event.inputBuffer;
        const inputData = inputBuffer.getChannelData(0); // MONO channel

        // EXACT SAME RMS CALCULATION AS PYTHON
        let sum = 0;
        for (let i = 0; i < inputData.length; i++) {
          sum += inputData[i] * inputData[i]; // Square each sample
        }
        const rms = Math.sqrt(sum / inputData.length); // Mean and square root

        // EXACT SAME DBFS CONVERSION AS PYTHON
        const dbfs = 20 * Math.log10(rms + 1e-10); // Same epsilon value

        // EXACT SAME CALIBRATION AND ADJUSTMENT AS PYTHON
        const db_spl = dbfs + CALIBRATION_OFFSET;
        const value = parseFloat((db_spl - 33).toFixed(2)); // EXACT same rounding (2 decimal places)

        // EXACT SAME THROTTLING LOGIC AS PYTHON
        const now = Date.now();
        if (now - lastSentRef.current >= INTERVAL) {
          const MAX_VALUE = 105.4; // Assuming a max value for normalization
          setCurrentDb(value > MAX_VALUE ? MAX_VALUE : value);

          // EXACT SAME SERVER EMISSION AS PYTHON
          socketRef.current.emit("decibel_value", {
            value: value > MAX_VALUE ? MAX_VALUE : value,
          });
          console.log(
            `📡 Sent: ${value > MAX_VALUE ? MAX_VALUE : value} dB SPL`,
          );

          lastSentRef.current = now;
        }
      };

      setIsRecording(true);
      console.log("🎤 Recording started with Python-exact algorithm");
    } catch (err: any) {
      console.error("Error accessing microphone:", err);
      setError(`Microphone access denied: ${err.message}`);
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.onaudioprocess = null;
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }

    if (microphoneRef.current) {
      microphoneRef.current.disconnect();
      microphoneRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsRecording(false);
    setCurrentDb(0);
    console.log("⏹️ Recording stopped");
  };

  const getDbColor = (db: number): string => {
    if (db < 50) return "text-green-500 border-green-500";
    if (db < 70) return "text-yellow-500 border-yellow-500";
    if (db < 85) return "text-orange-500 border-orange-500";
    return "text-red-500 border-red-500";
  };

  const getDbLevel = (db: number): string => {
    if (db < 30) return "Very Quiet";
    if (db < 50) return "Quiet";
    if (db < 65) return "Moderate";
    if (db < 80) return "Loud";
    return "Very Loud";
  };

  const getBgColor = (db: number): string => {
    if (db < 50) return "bg-green-100";
    if (db < 70) return "bg-yellow-100";
    if (db < 85) return "bg-orange-100";
    return "bg-red-100";
  };

  return (
    <div className="max-w-2xl mx-auto overflow-hidden">
      {/* Controls */}
      <div className="p-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={startRecording}
            disabled={isRecording || !isConnected}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white font-semibold rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
          >
            🎤 Connect Microphone
          </button>
          <button
            onClick={stopRecording}
            disabled={!isRecording}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-semibold rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
          >
            ⏹️ Disconnect Microphone
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-6 mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-800">
            <span className="text-lg">⚠️</span>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Display */}
      <div className="p-8">
        <div
          className={`flex flex-col items-center justify-center p-8 border-4 rounded-2xl transition-all duration-300 ${getDbColor(
            currentDb,
          )} ${getBgColor(currentDb)}`}
        >
          <div
            className={`text-6xl font-bold mb-2 transition-colors duration-300 ${
              getDbColor(currentDb).split(" ")[0]
            }`}
          >
            {currentDb.toFixed(1)}
          </div>
          <div className="text-xl text-gray-600 mb-2">dB SPL</div>
          <div
            className={`text-lg font-semibold transition-colors duration-300 ${
              getDbColor(currentDb).split(" ")[0]
            }`}
          >
            {getDbLevel(currentDb)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioCaptureSender;
